import axios from 'axios';
import { getValidToken } from './utils/auth';
import { notifyUnauthorized } from './utils/authSession';

function getClientSessionId() {
  try {
    const k = 'vitra_client_session_id';
    let v = sessionStorage.getItem(k);
    if (!v) {
      v = crypto.randomUUID();
      sessionStorage.setItem(k, v);
    }
    return v;
  } catch {
    return crypto.randomUUID();
  }
}

const API_ORIGIN = (() => {
  try {
    const base = process.env.REACT_APP_API_URL || '';
    if (!base) return null;
    return new URL(base, window.location.origin).origin;
  } catch {
    return null;
  }
})();

function isFirstPartyUrl(url) {
  try {
    const u = new URL(url, window.location.origin);
    if (u.origin === window.location.origin) return true;
    if (API_ORIGIN && u.origin === API_ORIGIN) return true;
    return false;
  } catch {
    return true;
  }
}

function isAuthCredentialRequest(url) {
  try {
    const path = new URL(url, window.location.origin).pathname.toLowerCase();
    return (
      path.includes('/auth/login') ||
      path.includes('/auth/signup') ||
      path.includes('/auth/register') ||
      path.includes('/auth/forgot') ||
      path.includes('/auth/reset')
    );
  } catch {
    return false;
  }
}

function mergeApiHeaders(headersLike) {
  const h = new Headers(headersLike || {});
  if (!h.has('X-Request-ID')) {
    h.set('X-Request-ID', crypto.randomUUID());
  }
  if (!h.has('X-Client-Session-ID')) {
    h.set('X-Client-Session-ID', getClientSessionId());
  }
  if (!h.has('Authorization')) {
    const token = getValidToken();
    if (token) {
      h.set('Authorization', `Bearer ${token}`);
    }
  }
  return h;
}

function installAxiosAuthAndComplianceHeaders() {
  axios.interceptors.request.use((config) => {
    const fullUrl = config.url
      ? (config.baseURL ? `${config.baseURL}${config.url}` : config.url)
      : config.baseURL;
    if (fullUrl && !isFirstPartyUrl(fullUrl)) {
      return config;
    }
    config.headers = config.headers || {};
    const hdrs = config.headers;
    if (!hdrs['X-Request-ID'] && !hdrs['x-request-id']) {
      hdrs['X-Request-ID'] = crypto.randomUUID();
    }
    if (!hdrs['X-Client-Session-ID'] && !hdrs['x-client-session-id']) {
      hdrs['X-Client-Session-ID'] = getClientSessionId();
    }
    if (!hdrs.Authorization && !hdrs.authorization) {
      const token = getValidToken();
      if (token) {
        hdrs.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const cfg = error?.config;
      const fullUrl = cfg?.url
        ? (cfg.baseURL ? `${cfg.baseURL}${cfg.url}` : cfg.url)
        : cfg?.baseURL;
      if (
        status === 401 &&
        fullUrl &&
        isFirstPartyUrl(fullUrl) &&
        !isAuthCredentialRequest(fullUrl)
      ) {
        notifyUnauthorized();
      }
      return Promise.reject(error);
    },
  );
}

function installFetchAuthAndComplianceHeaders() {
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const requestUrl =
      typeof Request !== 'undefined' && input instanceof Request
        ? input.url
        : (typeof input === 'string' ? input : String(input?.url || input || ''));

    if (!isFirstPartyUrl(requestUrl)) {
      return nativeFetch(input, init);
    }

    let response;
    if (typeof Request !== 'undefined' && input instanceof Request) {
      const merged = new Request(input, {
        headers: mergeApiHeaders(input.headers),
      });
      response = await nativeFetch(merged, init);
    } else {
      const headers = mergeApiHeaders(init.headers);
      response = await nativeFetch(input, { ...init, headers });
    }

    if (response.status === 401 && !isAuthCredentialRequest(requestUrl)) {
      notifyUnauthorized();
    }
    return response;
  };
}

export function installComplianceClient() {
  if (typeof window === 'undefined' || window.__vitraComplianceInstalled) {
    return;
  }
  window.__vitraComplianceInstalled = true;
  installAxiosAuthAndComplianceHeaders();
  installFetchAuthAndComplianceHeaders();
}
